import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OrderStatus } from '@proxy/enums';
import { SommelierService } from '@proxy/sommeliers';
import { Temporal } from '@js-temporal/polyfill';

type DeliveryPeriod = 'last30days' | 'thisMonth' | 'thisYear';

interface DeliveryOrder {
    id: number;
    wineryName: string;
    status: string;
    itemCount: number;
    totalPrice: number;
    orderNumber: string;
    creationTime: string;
}

@Component({
    selector: 'app-deliveries',
    templateUrl: './deliveries.component.html',
    standalone: false
})
export class DeliveriesComponent implements OnInit {
    orders: DeliveryOrder[] = [];
    loading = false;
    dateRange = {
        start: null as Temporal.PlainDate | null,
        end: null as Temporal.PlainDate | null,
    };

    constructor(
        private sommelierService: SommelierService,
        private route: ActivatedRoute,
        private router: Router
    ) { }

    ngOnInit(): void {
        this.initializeDateRangeFromQuery();
        this.loadData();
    }

    onDateRangeChange(range: { start: Temporal.PlainDate | null; end: Temporal.PlainDate | null }): void {
        this.dateRange = {
            start: range.start,
            end: range.end
        };
        this.updateQueryParams();
        this.loadData();
    }

    loadData(): void {
        this.loading = true;

        let from: string | undefined;
        let to: string | undefined;

        if (this.dateRange.start) {
            from = new Date(
                this.dateRange.start.year,
                this.dateRange.start.month - 1,
                this.dateRange.start.day
            ).toISOString();
        }

        if (this.dateRange.end) {
            to = new Date(
                this.dateRange.end.year,
                this.dateRange.end.month - 1,
                this.dateRange.end.day,
                23, 59, 59, 999
            ).toISOString();
        }

        this.sommelierService.getMyDeliveries({ from, to } as any).subscribe({
            next: (res) => {
                this.orders = res.map(item => ({
                    id: item.id,
                    wineryName: item.customerName || 'Cliente',
                    status: this.formatOrderStatus(item.orderStatus),
                    itemCount: item.itemCount || 0,
                    totalPrice: item.total || 0,
                    orderNumber: item.id.toString(),
                    creationTime: String(item.paidAt)
                }));
                this.loading = false;
            },
            error: (err) => {
                console.error(err);
                this.loading = false;
            }
        });
    }

    private initializeDateRangeFromQuery(): void {
        const params = this.route.snapshot.queryParams;
        const today = Temporal.Now.plainDateISO();

        let start: Temporal.PlainDate | null = today;
        let end: Temporal.PlainDate | null = today;

        if (params['start']) {
            const parsed = this.parseDateFromQuery(params['start']);
            if (parsed) start = parsed;
        }

        if (params['end']) {
            const parsed = this.parseDateFromQuery(params['end']);
            if (parsed) end = parsed;
        }

        const periodParam = this.parsePeriodParam(params['period']);
        if (periodParam) {
            const periodStart = this.getStartDateForPeriod(periodParam, today);
            if (periodStart) {
                start = periodStart;
                end = today;
            }
        }

        this.dateRange = {
            start,
            end
        };

        this.updateQueryParams();
    }

    private parsePeriodParam(period: string | undefined): DeliveryPeriod | null {
        if (!period) return null;
        if (period === 'last30days' || period === 'thisMonth' || period === 'thisYear') {
            return period;
        }
        return null;
    }

    private getStartDateForPeriod(period: DeliveryPeriod, today: Temporal.PlainDate): Temporal.PlainDate {
        switch (period) {
            case 'last30days':
                return today.subtract({ days: 30 });
            case 'thisMonth':
                return Temporal.PlainDate.from({ year: today.year, month: today.month, day: 1 });
            case 'thisYear':
                return Temporal.PlainDate.from({ year: today.year, month: 1, day: 1 });
        }
    }

    private updateQueryParams(): void {
        const queryParams: any = {};

        if (this.dateRange.start) {
            queryParams['start'] = this.formatDateForQuery(this.dateRange.start);
        } else {
            queryParams['start'] = null;
        }

        if (this.dateRange.end) {
            queryParams['end'] = this.formatDateForQuery(this.dateRange.end);
        } else {
            queryParams['end'] = null;
        }

        this.router.navigate([], {
            relativeTo: this.route,
            queryParams,
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    private formatDateForQuery(date: Temporal.PlainDate): string {
        return `${String(date.day).padStart(2, '0')}/${String(date.month).padStart(2, '0')}/${date.year}`;
    }

    private parseDateFromQuery(dateString: string): Temporal.PlainDate | null {
        try {
            const parts = dateString.split('/');
            if (parts.length === 3) {
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10);
                const year = parseInt(parts[2], 10);
                return Temporal.PlainDate.from({ year, month, day });
            }
            if (dateString.includes('-')) {
                return Temporal.PlainDate.from(dateString);
            }
        } catch {
            return null;
        }
        return null;
    }

    private formatOrderStatus(status: OrderStatus | undefined): string {
        if (status === undefined) return 'Unknown';
        switch (status) {
            case OrderStatus.Pending:
                return '::OrderPending';
            case OrderStatus.Paid:
                return '::Paid';
            case OrderStatus.Shipped:
                return '::Shipped';
            case OrderStatus.Delivered:
                return '::Delivered';
            case OrderStatus.Cancelled:
                return '::Cancelled';
            default:
                return 'Unknown';
        }
    }
}
